import { Link } from "react-router-dom";
import { Zap, Infinity as InfIcon, ShieldCheck, Star, Tag, Sparkles } from "lucide-react";
import { formatPriceLocal as formatPrice } from "../lib/api";
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import SmartImage from "./SmartImage";

const LABEL_MAP = {
  instant_delivery: { icon: Zap, text: "Okamžité doručení", color: "!text-blue-700" },
  lifetime: { icon: InfIcon, text: "Trvalá", color: "!text-emerald-700" },
  online_activation: { icon: ShieldCheck, text: "Online aktivace", color: "!text-violet-700" },
  bestseller: { icon: Star, text: "Nejprodávanější", color: "!text-amber-700" },
  sale: { icon: Tag, text: "Akce", color: "!text-orange-700" },
  new: { icon: Sparkles, text: "Novinka", color: "!text-pink-700" },
};

export default function ProductCard({ product }) {
  const { add } = useCart();
  const { t } = useI18n();
  const discount = product.price_original ? Math.round((1 - product.price / product.price_original) * 100) : 0;
  const inStock = (product.stock_count ?? 99) > 0;

  return (
    <div className="product-card rounded-2xl overflow-hidden flex flex-col" data-testid={`product-card-${product.slug}`}>
      <Link to={`/produkt/${product.slug}`} className="block aspect-square relative">
        {discount > 0 && <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-orange-500 text-white text-[11px] font-bold shadow-lg shadow-orange-500/40 z-10">-{discount}%</div>}
        <SmartImage
          testId={`product-card-image-${product.slug}`}
          wrapperClassName="absolute inset-0 flex items-center justify-center p-3"
          candidates={[...(product.images || []), product.box_mockup]}
          alt={product.name}
          className="max-h-full max-w-full object-contain"
          fallback={
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center" data-testid={`product-image-fallback-${product.slug}`}>
              <div className="w-14 h-14 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center shadow-sm mb-2">
                <span className="font-display font-bold text-xl text-zinc-400">{(product.brand || product.name || "?").charAt(0).toUpperCase()}</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">{product.brand}</div>
              <div className="text-sm font-semibold text-zinc-600 mt-1 line-clamp-2">{product.name}</div>
            </div>
          }
        />
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">{product.brand}</div>
        <Link to={`/produkt/${product.slug}`} className="font-display font-semibold text-zinc-900 leading-tight text-lg mb-2 hover:text-blue-700 transition-colors line-clamp-2">{product.name}</Link>
        <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{product.short_description}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(product.labels || []).slice(0, 3).map((l) => {
            const meta = LABEL_MAP[l];
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <span key={l} className={`label-pill ${meta.color}`}><Icon className="w-3 h-3"/>{meta.text}</span>
            );
          })}
          {(product.custom_labels || []).slice(0, 4).map((cl, i) => (
            <span
              key={`cl-${i}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-sm"
              style={{ background: cl.color || "#2563EB" }}
              data-testid={`product-custom-label-${product.slug}-${i}`}
            >
              {cl.text}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-end justify-between gap-3 pt-2 border-t border-zinc-100">
          <div>
            {product.price_original && <div className="text-xs text-zinc-400 line-through">{formatPrice(product.price_original)}</div>}
            <div className="font-display font-bold text-2xl text-zinc-900">{formatPrice(product.price)}</div>
          </div>
          <button
            onClick={() => add(product)}
            disabled={!inStock}
            className="btn-primary px-4 py-2.5 rounded-full text-sm font-semibold disabled:bg-zinc-300 disabled:shadow-none disabled:cursor-not-allowed"
            data-testid={`add-to-cart-${product.slug}`}
          >
            {inStock ? t("add_to_cart") : t("out_of_stock")}
          </button>
        </div>
      </div>
    </div>
  );
}
